import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { SearchNormal } from "iconsax-react";

import MobileLayout from "../../components/MobileLayout";
import { useSinalario, DataTypeVerbete } from "../../hooks/sinalario";
import { useGamification } from "../../hooks/useGamification";
import { VerbeteCard } from "../Sinalario/SinalarioList/styles";
import { TAB_BAR } from "../../constants/tabBar";
import {
  filterVerbetesByCategory,
  type DictionaryCategoryId,
  DICTIONARY_CATEGORIES,
} from "../../services/dictionaryCategories";

import { PageBody, SearchBox, Hint, ListWrap, EmptyHint } from "./styles";

function getVariantInfo(palavra: string): {
  isVariant: boolean;
  variantNumber: number | null;
} {
  if (!palavra?.trim()) return { isVariant: false, variantNumber: null };
  const re = /^(.+?)(\d+)$/;
  const match = re.exec(palavra.trim());
  if (!match) return { isVariant: false, variantNumber: null };
  const num = Number.parseInt(match[2], 10);
  return { isVariant: num > 0, variantNumber: num > 0 ? num : null };
}

const SearchPage: React.FC = () => {
  const { getVerbete } = useSinalario();
  const navigate = useNavigate();
  const { addPoints } = useGamification();
  const [all, setAll] = useState<DataTypeVerbete[]>([]);
  const [input, setInput] = useState("");
  const [results, setResults] = useState<DataTypeVerbete[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [category, setCategory] = useState<DictionaryCategoryId | "">("");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const awardedSearch = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await getVerbete();
        if (!cancelled) setAll(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) setAll([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getVerbete]);

  const runSearch = useCallback(
    (term: string, cat: DictionaryCategoryId | "" = category) => {
      const t = term.trim().toLowerCase();
      let pool = all;
      if (cat) {
        pool = filterVerbetesByCategory(all, cat);
      }
      if (t.length === 0) {
        setResults(cat ? pool : []);
        setHasSearched(Boolean(cat));
        return;
      }
      const r = pool.filter(
        (v) =>
          v?.palavra &&
          (v.palavra.toLowerCase().includes(t) ||
            (v.descricao || "").toLowerCase().includes(t))
      );
      setResults(r);
      setHasSearched(true);
      if (!awardedSearch.current && t.length >= 2) {
        awardedSearch.current = true;
        addPoints("SEARCH");
      }
    },
    [all, category, addPoints]
  );

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (input.trim().length === 0) {
      setResults([]);
      setHasSearched(false);
    } else {
      timeoutRef.current = setTimeout(() => runSearch(input), 450);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [input, runSearch]);

  const openDetails = (v: DataTypeVerbete) => {
    navigate("/VerbeteDetails", { state: v });
  };

  const showEmpty = useMemo(
    () => hasSearched && results.length === 0 && !loading,
    [hasSearched, results.length, loading]
  );

  return (
    <MobileLayout headerTitle="Buscar" headerType="private">
      <PageBody>
        <SearchBox>
          <SearchNormal size={22} color={TAB_BAR.primary} variant="Bulk" />
          <input
            type="search"
            placeholder="Digite uma palavra em Libras..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            enterKeyHint="search"
            autoComplete="off"
          />
        </SearchBox>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <button
            type="button"
            onClick={() => {
              setCategory("");
              runSearch(input, "");
            }}
            style={{
              borderRadius: 999,
              padding: "6px 12px",
              border: category === "" ? "none" : "1px solid #e2e8f0",
              background: category === "" ? "#4614BB" : "#fff",
              color: category === "" ? "#fff" : "#1e293b",
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Todas
          </button>
          {DICTIONARY_CATEGORIES.map((c) => {
            const active = category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setCategory(c.id);
                  runSearch(input, c.id);
                }}
                style={{
                  borderRadius: 999,
                  padding: "6px 12px",
                  border: active ? "none" : `1px solid ${c.color}55`,
                  background: active ? c.color : "#fff",
                  color: active ? "#fff" : c.color,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        <Hint>
          Busque por palavra ou descrição. Filtre por tema e ganhe XP ao
          pesquisar.
        </Hint>
        {loading && <EmptyHint>Carregando dicionário...</EmptyHint>}
        {showEmpty && (
          <EmptyHint>Nenhum sinal encontrado para essa busca.</EmptyHint>
        )}
        {!loading && results.length > 0 && (
          <ListWrap>
            {results.map((verbete) => {
              const variant = getVariantInfo(verbete.palavra || "");
              return (
                <VerbeteCard
                  key={verbete.uid || verbete.palavra}
                  onClick={() => openDetails(verbete)}
                >
                  <div className="card-main">
                    <div className="media-box">
                      {verbete.image ? (
                        <img src={verbete.image} alt={verbete.palavra} />
                      ) : (
                        <div className="letter-fallback">{verbete.letra}</div>
                      )}
                    </div>
                    <div className="info">
                      <h3>{verbete.palavra}</h3>
                      {variant.isVariant && (
                        <span className="variant-tag">
                          Sinal {variant.variantNumber}
                        </span>
                      )}
                      <p>{verbete.descricao}</p>
                    </div>
                  </div>
                </VerbeteCard>
              );
            })}
          </ListWrap>
        )}
      </PageBody>
    </MobileLayout>
  );
};

export default SearchPage;
